import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { catchError, concat, map, mergeMap, of, switchMap, withLatestFrom } from "rxjs";
import { CustomerClient, SubscriptionQuoteClient } from "src/api-service";
import { normalizeError } from "../../../app-store-error.types";
import { selectSelectedCompany } from "../../../user/user.selectors";
import { CustomerActions } from "../../customer/customer.actions";
import { QuoteSubscriptionCreateActions } from "./quote-subscription-create.actions";

@Injectable()
export class QuoteSubscriptionCreateEffects {

    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly subscriptionQuoteClient = inject(SubscriptionQuoteClient);
    private readonly customerClient = inject(CustomerClient);

    // =========================================================================
    // PRICE ESTIMATE
    // =========================================================================

    loadPriceEstimate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteSubscriptionCreateActions.loadPriceEstimate),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ request }, selectedCompany]) =>
                this.subscriptionQuoteClient.getPriceEstimate(request, selectedCompany.id).pipe(
                    map(response => QuoteSubscriptionCreateActions.loadPriceEstimateSuccess({
                        response,
                        toastKey: "",
                    })),
                    catchError(error => of(QuoteSubscriptionCreateActions.loadPriceEstimateFailure({
                        error: normalizeError(error),
                        toastKey: "SUBSCRIPTION.TOAST.PRICE_ESTIMATE_ERROR",
                    }))),
                ),
            ),
        ),
    );

    // =========================================================================
    // ATTIO DEAL → CUSTOMER
    // =========================================================================

    /**
     * The deal decides the customer: its company's Core customer code, looked up in the current company.
     * The customer is selected as the credit check's customer, which the next steps read.
     */
    loadDealCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteSubscriptionCreateActions.selectDeal),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ deal }, selectedCompany]) => {
                const clear = of(CustomerActions.selectCustomer({ customer: undefined }));
                if (!deal?.customerCode) {
                    return clear;
                }
                return concat(clear, this.customerClient.get(deal.customerCode, selectedCompany.id).pipe(
                    mergeMap(customer => [
                        CustomerActions.selectCustomer({ customer }),
                        QuoteSubscriptionCreateActions.loadDealCustomerSuccess(),
                    ]),
                    catchError(error => of(QuoteSubscriptionCreateActions.loadDealCustomerFailure({ error: normalizeError(error) }))),
                ));
            }),
        ),
    );
}
